import { useViewModel } from "mvvm/View";
import { PageViewModel } from "../ViewModels/PageViewModel";
import { JobListView } from "./JobListView";
import { QuestionView } from "./QuestionView";
import { JobDetailView } from "./JobDetailView";
import { HeaderView } from "./HeaderView";
import { SettingsView } from "../../../assets/utilities/SettingsView";
import { ScheduleView } from "../../../assets/utilities/ScheduleView";
import { QueriesView } from "../../../assets/utilities/QueriesView";
import { SavedQueriesView } from "../../../assets/utilities/SavedQueriesView";
import { useEffect, useState, useCallback, useRef } from "react";
import Model from "redux/classes/Model";
import Session from "redux/classes/Session";
import { useAppSelector, useDarkMode, useThemeColors, useAppDispatch } from "redux/hooks";
import { UpdateSettingsAction } from "redux/actions/UpdateSettings";

export function PageView(props: { model: PageViewModel; allModels: Model[]; currentModelAlias: string; session: Session; onLogout: () => void; isSettingsOpen: boolean; onSettingsToggle: () => void; isScheduleOpen: boolean; onScheduleToggle: () => void; isQueriesOpen: boolean; onQueriesToggle: () => void; isAddQueryOpen: boolean; queriesInitialQuery?: string; queriesInitialQueryName?: string; onAddQueryToggle: (query?: string, queryName?: string) => void }) {
    const model = useViewModel(props.model);
    const dispatch = useAppDispatch();
    const selectedJob = model.selectedJob;
    const darkMode = useDarkMode();
    const themeColors = useThemeColors();
    const stripPrefix = (name: string) => {
        return name.replace(/^QUERYSTREAM\s*-\s*/i, '');
    };

    // Get the root parent job alias (walk up the chain as far as possible)
    const getRootParentJobAlias = () => {
        if (!selectedJob) return undefined;

        let currentJob = selectedJob;
        let parentID = currentJob.replyTarget ? currentJob.replyTarget.split('-').slice(1).join('-') : undefined;

        while (parentID) {
            const parentJob = model.jobs.find(job => job.jobID === parentID);
            if (!parentJob) {
                break;
            }
            currentJob = parentJob;
            parentID = parentJob.replyTarget ? parentJob.replyTarget.split('-').slice(1).join('-') : undefined;
        }

        return currentJob.alias;
    };

    const parentJobAlias = getRootParentJobAlias();

    const [tagList, setTagList] = useState<string[]>([]);
    const [anyContextMenuOpen, setAnyContextMenuOpen] = useState(false);
    const showLeftnavSetting = useAppSelector(state => state.user.settings?.show_leftnav ?? true);
    const [leftPanelWidth, setLeftPanelWidth] = useState(400); // Default width in pixels
    const [isLeftPanelCollapsed, setIsLeftPanelCollapsed] = useState(!showLeftnavSetting);
    const [expandedLeftPanelWidth, setExpandedLeftPanelWidth] = useState(400);
    const [isResizing, setIsResizing] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [shouldAnimate, setShouldAnimate] = useState(false);
    const [isSelectedJobAnimating, setIsSelectedJobAnimating] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const updateTagList = useCallback(() => {
        let allTags: string[] = []
        for (let job of model.jobs) {
            for (let tag of job.tags) {
                if (!allTags.includes(tag)) {
                    allTags.push(tag);
                }
            }
        }
        setTagList(allTags.sort());
    }, [model.jobs]);

    const updateJobTag = useCallback((jobID: string, tags: string[]) => {
        const job = model.jobs.find(job => job.jobID === jobID);
        if (job) {
            job.tags = tags;
        }
        updateTagList();
    }, [model.jobs, updateTagList]);

    // Subscribe to each job's updates to detect tag changes
    useEffect(() => {
        const unsubscribes: (() => void)[] = [];

        // Subscribe to each job's view updates
        for (const job of model.jobs) {
            const callback = () => {
                // When any job updates, refresh the tag list
                updateTagList();
            };
            job.addView(callback);
            unsubscribes.push(() => job.removeView(callback));
        }

        // Initial update
        updateTagList();

        return () => {
            unsubscribes.forEach(unsub => unsub());
        };
    }, [model.jobs, updateTagList])

    const handleMouseDown = useCallback(() => {
        setIsResizing(true);
    }, []);

    const handleMouseMove = useCallback((e: MouseEvent) => {
        if (!isResizing || !containerRef.current) return;

        const containerRect = containerRef.current.getBoundingClientRect();
        const newWidth = e.clientX - containerRect.left;

        // Min width: 250px, Max width: 80% of container
        const minWidth = 250;
        const maxWidth = containerRect.width * 0.8;

        const constrainedWidth = Math.max(minWidth, Math.min(newWidth, maxWidth));
        setLeftPanelWidth(constrainedWidth);
        setExpandedLeftPanelWidth(constrainedWidth);
        setIsLeftPanelCollapsed(false);
    }, [isResizing, containerRef]);

    const handleMouseUp = useCallback(() => {
        setIsResizing(false);
    }, []);

    const handleToggleLeftPanelCollapse = useCallback(() => {
        setIsLeftPanelCollapsed(prev => {
            const nextCollapsed = !prev;
            if (nextCollapsed) {
                setExpandedLeftPanelWidth(leftPanelWidth);
                setLeftPanelWidth(0);
            } else {
                setLeftPanelWidth(expandedLeftPanelWidth || 400);
            }
            // Persist setting outside the state updater to avoid dispatching during render
            setTimeout(() => dispatch(UpdateSettingsAction(props.session, { show_leftnav: !nextCollapsed })), 0);
            return nextCollapsed;
        });
    }, [leftPanelWidth, expandedLeftPanelWidth, dispatch, props.session]);

    const handleExpandLeftPanel = useCallback(() => {
        setIsLeftPanelCollapsed(false);
        setLeftPanelWidth(expandedLeftPanelWidth || 400);
        dispatch(UpdateSettingsAction(props.session, { show_leftnav: true }));
    }, [expandedLeftPanelWidth, dispatch, props.session]);

    useEffect(() => {
        if (isResizing) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = 'col-resize';
            document.body.style.userSelect = 'none';

            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
                document.body.style.cursor = '';
                document.body.style.userSelect = '';
            };
        }
    }, [isResizing, handleMouseMove, handleMouseUp]);

    // Sync left nav collapsed state from persisted setting
    // Only react to showLeftnavSetting changes — not width values, which would create a feedback loop
    useEffect(() => {
        if (showLeftnavSetting) {
            setIsLeftPanelCollapsed(false);
            setLeftPanelWidth(prev => prev || 400);
        } else {
            setIsLeftPanelCollapsed(true);
            setLeftPanelWidth(prev => {
                if (prev > 0) setExpandedLeftPanelWidth(prev);
                return 0;
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showLeftnavSetting]);

    // Trigger animation when centered view appears (on mount or when selectedJob becomes undefined)
    useEffect(() => {
        if (!selectedJob) {
            // Reset animation state and trigger animation
            setShouldAnimate(false);
            // Use a small delay to ensure DOM is ready
            const timeoutId = setTimeout(() => {
                setShouldAnimate(true);
            }, 10);
            return () => clearTimeout(timeoutId);
        } else {
            setShouldAnimate(false);
        }
    }, [selectedJob]);

    return (
        <div ref={containerRef} className="flex-1 flex-row" style={{ background: themeColors.backgroundColor, position: "relative", color: themeColors.textColor }}>
            {/* Left Panel - QueryStream */}
            <div style={{ width: `${leftPanelWidth}px`, flexShrink: 0, display: isLeftPanelCollapsed ? "none" : "block" }}>
                <JobListView
                    jobs={model.jobs}
                    tagList={tagList}
                    anyContextMenuOpen={anyContextMenuOpen}
                    setAnyContextMenuOpen={setAnyContextMenuOpen}
                    onNewStream={() => model.selectedJob = undefined}
                    modelAlias={props.currentModelAlias}
                    jobService={model.jobService}
                    onToggleCollapse={handleToggleLeftPanelCollapse}
                    isCollapsed={isLeftPanelCollapsed}
                />
            </div>

            {/* Resizable Divider */}
            {!isLeftPanelCollapsed && (
                <div
                    onMouseDown={handleMouseDown}
                    style={{
                        width: "2px",
                        cursor: "col-resize",
                        backgroundColor: themeColors.borderColor,
                        flexShrink: 0,
                        position: "relative",
                        zIndex: 10
                    }}
                />
            )}

            {/* Right Panel - Header, Response, and Request */}
            <div className="flex-1 flex-column" style={{ display: "flex", flexDirection: "column", overflow: "hidden" }}>
                {/* Header */}
                <div style={{ position: "relative", zIndex: 1060 }}>
                    <HeaderView
                        allModels={props.allModels}
                        currentModelAlias={props.currentModelAlias}
                        selectedJobName={parentJobAlias}
                        session={props.session}
                        onLogout={props.onLogout}
                        onSettingsClick={props.onSettingsToggle}
                        onScheduleClick={props.onScheduleToggle}
                        onQueriesClick={() => props.onQueriesToggle()}
                        isLeftPanelCollapsed={isLeftPanelCollapsed}
                        onExpandLeftPanel={handleExpandLeftPanel}
                    />
                </div>

                {/* Response */}
                <div ref={scrollContainerRef} className="flex-1" style={{ minHeight: "12em", overflow: "auto", display: "flex", flexDirection: "column", position: "relative" }}>
                    <style>{`
                        .ant-pagination-options-size-changer .ant-select-dropdown {
                            z-index: 1050 !important;
                        }
                        @keyframes slideInFromBelow {
                            from {
                                transform: translateY(100vh);
                                opacity: 0;
                            }
                            to {
                                transform: translateY(0);
                                opacity: 1;
                            }
                        }
                        .centered-label-animate {
                            animation: slideInFromBelow 1s ease-out forwards;
                        }
                        .centered-input-animate {
                            animation: slideInFromBelow 1s ease-out forwards;
                        }
                        .centered-label-initial {
                            transform: translateY(100vh);
                            opacity: 0;
                        }
                        .centered-input-initial {
                            transform: translateY(100vh);
                            opacity: 0;
                        }
                    `}</style>
                    { selectedJob && <JobDetailView model={selectedJob} tagList={tagList} tagUpdater={updateJobTag} onEditModeChange={setIsEditMode} scrollContainerRef={scrollContainerRef} parentJobAlias={parentJobAlias} onSelectedJobAnimationChange={setIsSelectedJobAnimating} onAddQueryToggle={props.onAddQueryToggle} />}

                    {/* Centered welcome view when no job selected */}
                    {!selectedJob && (
                        <div style={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            flex: 1,
                            padding: "2em",
                            overflow: "hidden"
                        }}>
                            <div style={{
                                maxWidth: "900px",
                                width: "100%",
                                textAlign: "center"
                            }}>
                                <h2 className={shouldAnimate ? "centered-label-animate" : "centered-label-initial"} style={{
                                    marginBottom: "0.75em",
                                    color: themeColors.textColor,
                                    fontWeight: "400",
                                    fontSize: "1.5em"
                                }}>
                                    Ask anything about your {stripPrefix(props.allModels.find(m => m.alias === props.currentModelAlias)?.name || "")} data
                                </h2>
                                <div className={shouldAnimate ? "centered-input-animate" : "centered-input-initial"}>
                                    <QuestionView model={model.question} hasSelectedJob={false} centered={true} />
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Bottom Right - Request Input (only when job selected) */}
                {selectedJob && (
                    <div style={{ flexShrink: 0, position: "relative", zIndex: 1060 }}>
                        <QuestionView model={model.question} hasSelectedJob={true} centered={false} isAnimating={isSelectedJobAnimating} />
                    </div>
                )}
            </div>
            {/* Settings View */}
            <SettingsView isOpen={props.isSettingsOpen} onClose={props.onSettingsToggle} mode="stream" />
            {/* Schedule View */}
            <ScheduleView isOpen={props.isScheduleOpen} onClose={props.onScheduleToggle} model={props.model} mode="stream" />
            {/* Queries View (Saved Queries) */}
            <SavedQueriesView isOpen={props.isQueriesOpen} onClose={() => props.onQueriesToggle()} onOpenAddQuery={(query, queryName) => props.onAddQueryToggle(query, queryName)} isAddQueryOpen={props.isAddQueryOpen} />
            {/* Add Query View */}
            <QueriesView isOpen={props.isAddQueryOpen} onClose={() => {
                // Clear initial values when closing
                props.onAddQueryToggle(undefined, undefined);
            }} initialQuery={props.isAddQueryOpen ? props.queriesInitialQuery : undefined} initialQueryName={props.isAddQueryOpen ? props.queriesInitialQueryName : undefined} />
        </div>
    )
}